import { useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Mail, ArrowUpRight, ArrowUp } from "lucide-react";
import { trackFooterSocialClick } from "@/lib/analytics";
import useMotionPreference from "@/hooks/useMotionPreference";

const SOCIALS = [
  { icon: Github, href: "https://github.com/tanishq-sa", label: "github" },
  { icon: Linkedin, href: "https://www.linkedin.com/in/tanishq-saini7/", label: "linkedin" },
  { icon: Mail, href: "mailto:tanishq@tanishqsa.dev", label: "email" },
];

export default function Footer() {
  const reduceMotion = useMotionPreference();
  return (
    <footer data-testid="footer" className="border-t border-white/5 bg-[#070708]">
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-10">
        <div className="flex flex-col justify-between gap-10 md:flex-row md:items-end">
          <div>
            <a href="#top" className="font-display text-3xl font-bold tracking-tight text-white">
              Tanishq Saini<span className="text-brand-500">.</span>
            </a>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/55">
              Software Developer specializing in Frontend &amp; Java Backend. Building fast,
              scalable, user-focused software.
            </p>
          </div>
          <div className="flex flex-col items-start gap-5 md:items-end">
            <motion.a
              href="#top"
              data-testid="footer-back-to-top"
              whileHover={reduceMotion ? undefined : { y: -3 }}
              whileTap={reduceMotion ? undefined : { scale: 0.97 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className="focus-ring group inline-flex min-h-11 items-center gap-3 rounded-full border border-white/15 px-5 py-2.5 font-mono text-xs text-white/70 transition-colors hover:border-brand-500/50 hover:text-brand-300"
            >
              Back to top
              <ArrowUp aria-hidden="true" className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5" />
            </motion.a>
          <div className="flex gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-${s.label}`}
                aria-label={s.label}
                onClick={() => trackFooterSocialClick(s.label)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white/70 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500/40 hover:text-brand-400"
              >
                <s.icon className="h-5 w-5" />
              </a>
            ))}
          </div>
          </div>
        </div>

        <div className="mt-12 border-t border-white/5 pt-8 font-mono text-xs text-white/50 space-y-4">
          <p className="text-white/50 text-center">
            This site may collect form submissions and newsletter sign-ups (unique emails only) to respond to inquiries and send updates.
            You can unsubscribe at any time from the newsletter section. It also uses{" "}
            <a
              href="https://clarity.microsoft.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-white transition-colors"
            >
              Microsoft Clarity
            </a>{" "}
            for anonymous behavioral analytics (session recordings &amp; heatmaps). No personal data is shared with third parties.
          </p>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p>Built with React, Tailwind CSS &amp; Spring Boot.</p>
            <a
              href="https://github.com/tanishq-sa"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1.5 transition-colors hover:text-white"
            >
              Designed &amp; developed by Tanishq Saini
              <ArrowUpRight className="h-3.5 w-3.5 text-brand-500 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Let Contact slide off a stationary footer; use ordinary flow when space is tight. */
export function FooterReveal({ children }) {
  const footerRef = useRef(null);
  const focusRef = useRef(null);
  const reduceMotion = useMotionPreference();
  const [footerHeight, setFooterHeight] = useState(0);
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  const [keyboardMode, setKeyboardMode] = useState(false);

  useLayoutEffect(() => {
    const footer = footerRef.current;
    const measure = () => setFooterHeight(footer.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(footer);
    const resize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", resize);
    return () => { observer.disconnect(); window.removeEventListener("resize", resize); };
  }, []);

  useLayoutEffect(() => {
    // Focusable links stay in normal flow when reached with a keyboard.
    if (keyboardMode) focusRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" });
  }, [keyboardMode]);

  const reveal = !reduceMotion && !keyboardMode && viewport.width >= 1024 && footerHeight > 0 && footerHeight + 96 < viewport.height;
  return (
    <div data-testid="contact-footer-reveal" data-reveal={reveal} className="relative isolate">
      <div className="relative z-10 rounded-b-3xl bg-[#050505]">{children}</div>
      <div
        ref={footerRef}
        className="relative z-0"
        style={reveal ? { position: "sticky", bottom: 0 } : undefined}
        onFocusCapture={event => {
          if (!event.target.matches(":focus-visible")) return;
          focusRef.current = event.target;
          setKeyboardMode(true);
        }}
      >
        <Footer />
      </div>
    </div>
  );
}
